import {
  BOOKING_FULL_WITH_JOIN,
  BOOKING_WITH_EMPTY_EMAIL,
  BOOKING_WITH_INVALID_EMAIL,
  BOOKING_WITH_JOIN,
  BOOKING_WITH_NOTE,
  BOOKING_WITH_RES_DETAILS,
  CANCELED_USHG_BOOKING,
  CANCELED_WAITLIST_BOOKING,
  CANCELLED_BOOKING,
  COMPLEX_SPECIAL_OCCASIONS,
  CONFIRMED_BOOKING,
  DEFAULT_BOOKING,
  GUEST_BOOKING,
  GUEST_BOOKING_WITH_SPLIT,
  GUEST_CHECK_SPLITTING_NOT_ENABLED,
  GUEST_LIST_FULL_BOOKING,
  GUEST_LIST_MORE_GUESTS,
  GUEST_LIST_SOME_SPLITTING,
  GUEST_LIST_WITH_LEAD,
  GUEST_NOT_ATTENDING,
  INTEGRATION_TEST_BOOKING,
  INTEGRATION_TEST_CANCELLED_BOOKING,
  LEAD_BOOKING_ENROLLED,
  NO_CHECKLESS_PAYMENTS_BOOKING,
  NO_FEE_BOOKING,
  PER_RESERVATION_FEE_BOOKING,
  REDIRECT_TO_LIVE_CHECK,
  SPECIAL_REQUEST_STEP_1,
  SPECIAL_REQUEST_STEP_2,
  STARTED_BOOKING,
  UNENROLLED_BOOKING,
  UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA,
  UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA,
  UNENROLLMENT_FINAL_DATA,
  UNENROLLMENT_INITIAL_DATA,
  UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA,
  UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA,
  USHG_BOOKING,
  WAITLIST_BOOKING,
  WAITLIST_BOOKING_READY,
  WAITLIST_GUEST_LIST_FULL,
  WAITLIST_WAITTIME_OVER
} from 'mocks/data/shared/bookingIds';
import type {
  Booking,
  BookingQueryResponse,
  Fee,
  Guest,
  SavedPaymentMethodV2,
  Visit
} from 'reservations/booking/types';
import { RESERVATION, WAITPARTY } from 'reservations/booking/types';

export const generateBookingDetails = ({
  firstName = 'Jimmy',
  lastName = 'Jammer',
  email = 'jimmy.jammer@gmail.com',
  phone = '2125551212',
  phoneCode = '1',
  dateAndTime = new Date(Date.now() + 60 * 60 * 1000)
    .toISOString()
    .slice(0, 17)
    .concat('00'),
  partySize = 2,
  note = '',
  bookingId = DEFAULT_BOOKING,
  state = 'Active',
  tags = [],
  dietaryTags = [],
  optInMarketingEmail = false,
  optInMarketingSms = false,
  hasGuestConfirmed = false,
  positionInQueue = 0,
  type = RESERVATION,
  externalReservationUrl = '',
  waitEstimate = 0
}: Partial<Booking> = {}): Booking => ({
  firstName,
  lastName,
  email,
  phone,
  phoneCode,
  dateAndTime,
  partySize,
  note,
  bookingId,
  state,
  tags,
  dietaryTags,
  optInMarketingEmail,
  optInMarketingSms,
  hasGuestConfirmed,
  positionInQueue,
  type,
  externalReservationUrl,
  waitEstimate
});

export const generateFee = ({
  shouldCollect = true,
  collectType = 'CardOnFile',
  calcType = 'PartySize',
  fee = 2500,
  cancelDeadlineMinutes = 1000
}: Partial<Fee> = {}): Fee => ({
  calcType,
  collectType,
  fee,
  shouldCollect,
  cancelDeadlineMinutes
});

const today = new Date();
export const cardExpYear = today.getFullYear() + 1;
export const cardExpMonth = today.getMonth() + 1;

export const generateSavedPaymentMethod = ({
  id = '12332',
  card_type = 'VISA',
  card_last4 = '3294',
  card_expiration_month = cardExpMonth,
  card_expiration_year = cardExpYear
}: Partial<SavedPaymentMethodV2> = {}): SavedPaymentMethodV2 => ({
  id,
  card_type,
  card_last4,
  card_expiration_month,
  card_expiration_year
});

export const generateVisit = ({
  opt_in_checkless = true,
  lead = true,
  tip_percentage = 20,
  visit_id = 38,
  saved_payment_method = generateSavedPaymentMethod(),
  attending = true,
  check_splitting_enabled = true
}: Partial<Visit> = {}): Visit => ({
  opt_in_checkless,
  lead,
  tip_percentage,
  visit_id,
  saved_payment_method,
  attending,
  check_splitting_enabled
});

export const generateBooking = ({
  booking = generateBookingDetails(),
  fee = generateFee(),
  visit = generateVisit(),
  guests = []
}: Partial<BookingQueryResponse> = {}): BookingQueryResponse => ({
  booking,
  fee,
  visit,
  guests
});

export const generateGuest = ({
  id = 7,
  name = 'Jane Doe',
  lead = false,
  opt_in_checkless = false,
  check_splitting_enabled = false
}: Partial<Guest> = {}) => ({
  id,
  name,
  lead,
  opt_in_checkless,
  check_splitting_enabled
});

export const generateGuests = (numberOfGuests = 2): Array<Guest> => {
  const guests = new Array(numberOfGuests).fill(generateGuest());
  guests[0] = { ...guests[0], lead: true };
  return guests;
};

export const bookingByBookingId: Record<string, any> = {
  [DEFAULT_BOOKING]: generateBooking(),
  [BOOKING_WITH_EMPTY_EMAIL]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_EMPTY_EMAIL,
      email: ''
    })
  }),
  [BOOKING_WITH_INVALID_EMAIL]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_INVALID_EMAIL,
      email: 'invalid email'
    })
  }),
  [CANCELLED_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: CANCELLED_BOOKING,
      state: 'Canceled'
    })
  }),
  [BOOKING_WITH_NOTE]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_NOTE,
      note: 'Some note'
    })
  }),
  [STARTED_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: STARTED_BOOKING,
      state: 'Seated'
    })
  }),
  [WAITLIST_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: WAITLIST_BOOKING,
      type: WAITPARTY,
      positionInQueue: 2,
      waitEstimate: 15
    })
  }),
  [CANCELED_WAITLIST_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: CANCELED_WAITLIST_BOOKING,
      type: WAITPARTY,
      state: 'Canceled',
      positionInQueue: 2
    })
  }),
  [WAITLIST_BOOKING_READY]: generateBooking({
    booking: generateBookingDetails({
      bookingId: WAITLIST_BOOKING_READY,
      type: WAITPARTY,
      positionInQueue: 1
    })
  }),
  [WAITLIST_WAITTIME_OVER]: generateBooking({
    booking: generateBookingDetails({
      bookingId: WAITLIST_BOOKING_READY,
      type: WAITPARTY,
      positionInQueue: 3,
      waitEstimate: 0
    })
  }),
  [NO_FEE_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: NO_FEE_BOOKING
    }),
    fee: generateFee({
      shouldCollect: false
    }),
    visit: generateVisit({
      saved_payment_method: null
    })
  }),
  [NO_CHECKLESS_PAYMENTS_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: NO_CHECKLESS_PAYMENTS_BOOKING
    }),
    visit: generateVisit({
      opt_in_checkless: false
    })
  }),
  [PER_RESERVATION_FEE_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: PER_RESERVATION_FEE_BOOKING
    }),
    fee: generateFee({
      calcType: RESERVATION
    })
  }),
  [USHG_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: USHG_BOOKING,
      externalReservationUrl: 'some-external-url'
    })
  }),
  [CANCELED_USHG_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: CANCELED_USHG_BOOKING,
      externalReservationUrl: 'some-external-url',
      state: 'Canceled'
    })
  }),
  [BOOKING_WITH_RES_DETAILS]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_RES_DETAILS,
      tags: ['Birthday'],
      dietaryTags: ['Vegan'],
      note: 'This is a special request'
    })
  }),
  [COMPLEX_SPECIAL_OCCASIONS]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_RES_DETAILS,
      tags: ['birthday', 'bridal shower']
    })
  }),
  [UNENROLLED_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: UNENROLLED_BOOKING
    }),
    visit: generateVisit({
      opt_in_checkless: false
    })
  }),
  [INTEGRATION_TEST_BOOKING]: generateBooking({
    booking: generateBookingDetails({ bookingId: INTEGRATION_TEST_BOOKING }),
    fee: generateFee({
      calcType: RESERVATION
    })
  }),
  [INTEGRATION_TEST_CANCELLED_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: INTEGRATION_TEST_CANCELLED_BOOKING,
      state: 'Canceled'
    })
  }),
  [SPECIAL_REQUEST_STEP_1]: generateBooking({
    booking: generateBookingDetails({
      bookingId: SPECIAL_REQUEST_STEP_1,
      note: 'This is a new special request.'
    })
  }),
  [SPECIAL_REQUEST_STEP_2]: generateBooking({
    booking: generateBookingDetails({
      bookingId: SPECIAL_REQUEST_STEP_2,
      note: 'This is an updated special request.'
    })
  }),
  [UNENROLLMENT_INITIAL_DATA]: generateBooking({
    booking: generateBookingDetails({ bookingId: UNENROLLMENT_INITIAL_DATA }),
    visit: generateVisit(
      generateVisit({
        opt_in_checkless: false,
        tip_percentage: 0
      })
    )
  }),
  [UNENROLLMENT_FINAL_DATA]: generateBooking({
    booking: generateBookingDetails({ bookingId: UNENROLLMENT_FINAL_DATA }),
    visit: generateVisit(
      generateVisit({
        opt_in_checkless: true,
        tip_percentage: 22
      })
    )
  }),
  [UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA]: generateBooking({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_CUSTOM_GRATUITY_INITIAL_DATA
    }),
    visit: generateVisit({
      opt_in_checkless: false
    })
  }),
  [UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA]: generateBooking({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_CUSTOM_GRATUITY_FINAL_DATA
    }),
    visit: generateVisit({
      opt_in_checkless: true,
      tip_percentage: 12
    })
  }),
  [UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA]: generateBooking({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_UPDATE_GRATUITY_INITIAL_DATA
    }),
    visit: generateVisit({
      opt_in_checkless: true,
      tip_percentage: 22
    })
  }),
  [UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA]: generateBooking({
    booking: generateBookingDetails({
      bookingId: UNENROLLMENT_UPDATE_GRATUITY_FINAL_DATA
    }),
    visit: generateVisit({
      opt_in_checkless: true,
      tip_percentage: 20
    })
  }),
  [REDIRECT_TO_LIVE_CHECK]: generateBooking({
    booking: generateBookingDetails({
      bookingId: REDIRECT_TO_LIVE_CHECK
    }),
    visit: generateVisit({
      opt_in_checkless: true,
      tip_percentage: 18
    })
  }),
  [CONFIRMED_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: CONFIRMED_BOOKING,
      hasGuestConfirmed: true
    })
  }),

  [GUEST_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_BOOKING
    }),
    visit: generateVisit({
      lead: false
    })
  }),
  [GUEST_NOT_ATTENDING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_NOT_ATTENDING
    }),
    visit: generateVisit({
      lead: false,
      attending: false
    })
  }),
  [GUEST_CHECK_SPLITTING_NOT_ENABLED]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_CHECK_SPLITTING_NOT_ENABLED
    }),
    visit: generateVisit({
      lead: false,
      check_splitting_enabled: false
    })
  }),
  [GUEST_LIST_FULL_BOOKING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_LIST_FULL_BOOKING
    }),
    guests: generateGuests()
  }),
  [WAITLIST_GUEST_LIST_FULL]: generateBooking({
    booking: generateBookingDetails({
      bookingId: WAITLIST_GUEST_LIST_FULL,
      type: WAITPARTY
    }),
    guests: generateGuests()
  }),
  [LEAD_BOOKING_ENROLLED]: generateBooking({
    booking: generateBookingDetails({
      bookingId: LEAD_BOOKING_ENROLLED
    }),
    guests: [
      generateGuest({
        lead: true,
        opt_in_checkless: true
      })
    ]
  }),
  [GUEST_LIST_MORE_GUESTS]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_LIST_MORE_GUESTS
    }),
    guests: generateGuests(5)
  }),
  [GUEST_LIST_WITH_LEAD]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_LIST_WITH_LEAD
    }),
    guests: [
      generateGuest({ name: 'Jimmy Jammer', lead: true }),
      ...generateGuests()
    ]
  }),
  [GUEST_LIST_SOME_SPLITTING]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_LIST_SOME_SPLITTING
    }),
    guests: [
      generateGuest({
        name: 'Jimmy Jammer',
        lead: true,
        check_splitting_enabled: true,
        opt_in_checkless: true
      }),
      generateGuest({
        name: 'Judy Jammer',
        opt_in_checkless: true,
        check_splitting_enabled: true
      }),
      generateGuest({
        name: 'John Doe',
        opt_in_checkless: true,
        check_splitting_enabled: true
      }),
      ...generateGuests()
    ]
  }),
  [GUEST_BOOKING_WITH_SPLIT]: generateBooking({
    booking: generateBookingDetails({
      bookingId: GUEST_BOOKING_WITH_SPLIT
    }),
    visit: generateVisit({
      lead: false
    }),
    guests: [
      generateGuest({
        name: 'Jimmy Jammer',
        lead: true,
        check_splitting_enabled: true,
        opt_in_checkless: true
      }),
      generateGuest({
        name: 'Jimmy Jammer',
        lead: false,
        check_splitting_enabled: true,
        opt_in_checkless: true
      })
    ]
  }),
  [BOOKING_WITH_JOIN]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_WITH_JOIN,
      firstName: 'Greg',
      lastName: 'Dixon',
      email: 'gregdixon@getbento.com',
      phone: '2345678911',
      partySize: 3
    }),
    fee: generateFee({
      shouldCollect: false
    }),
    visit: generateVisit({
      opt_in_checkless: false,
      lead: true,
      tip_percentage: 0,
      check_splitting_enabled: false,
      saved_payment_method: null
    }),
    guests: [
      generateGuest({
        name: 'Greg Dixon',
        lead: true,
        check_splitting_enabled: true
      }),
      generateGuest({ name: 'Jane Doe' })
    ]
  }),

  [BOOKING_FULL_WITH_JOIN]: generateBooking({
    booking: generateBookingDetails({
      bookingId: BOOKING_FULL_WITH_JOIN,
      firstName: 'Greg',
      lastName: 'Dixon',
      email: 'gregdixon@getbento.com',
      phone: '2345678911',
      partySize: 3,
      hasGuestConfirmed: true
    }),
    guests: [
      generateGuest({
        name: 'Greg Dixon',
        lead: true,
        check_splitting_enabled: true
      }),
      generateGuest({ name: 'Jane Doe' }),
      generateGuest({ name: 'Johnny Depp' })
    ]
  })
};
